import type { VersionedWording } from '@/lib/versioned-labels';
import { METHOD_NAME } from '@matter_extractor/shared';

/**
 * How the versioned layer names « Méthode » (analyste.md §4, issue #196),
 * everywhere it has to.
 *
 * A SINGLETON, like the texts of the noyaux, the models and the rules beside it:
 * no list, no card, no creation box, no retirement, no rename. So half of
 * `VersionedWording` names gestures this object never offers - the `settings`
 * logement renders neither the retirement panel nor its box - and those sentences
 * are written anyway, in French and true, because a wording that lied in an
 * unreachable corner would be the first thing a later lot copies.
 *
 * The prefixes are `methode`: every mark of the head is `methode-...`
 * (`methode-entete`, `methode-enregistrer`, `methode-historique-ouvrir`), which is
 * what `reglages-methode.spec.ts` and `screenshots-reglages-methode.mjs` read,
 * beside the cards' own `methode-fonction` and `methode-style`. Unaccented, like
 * the four before it, so a spec can type them.
 */
export const METHOD_WORDING: VersionedWording = {
  kind: 'method',
  entity: 'methode',
  list: 'methode',
  noun: ['réglage de la méthode', 'réglages de la méthode'],
  newTitle: METHOD_NAME,
  createLabel: 'Écrire la méthode',
  saveHintNew: 'Crée la v1. Inactif tant que chaque fonction et chaque style ne sont pas remplis.',
  // The singleton is born at boot, from a seed: no server draft is ever missing.
  draftStateNew: 'Aucune version encore.',
  // Nothing cites Méthode, and a SESSION freezes the version it starts on - one whose
  // experience plays a Noyau block, and on the side it plays (K13, vitrine.md §3.2): the
  // promise is about the sessions to come, and says which.
  developmentHint: (nextNumber) =>
    `Les sessions neuves à bloc Noyau, jouées sur le développement, liront la v${nextNumber} ; les sessions en cours gardent la version qu’elles ont figée.`,
  subject: (name) => name,
  retirement: {
    panelTitle: 'Méthode ne se retire pas',
    retiredTitle: 'Méthode retirée',
    confirmLabel: 'Retirer la méthode',
    panelHint: () => (
      <>
        Un seul objet pour les trois fonctions et les six styles, sans retrait : l’analyste la lit
        toujours.
      </>
    ),
    retiredHint: (_name, day) => <>Retirée le {day}.</>,
    warningTitle: () => 'Aucune expérience à bloc Noyau ne pourrait plus démarrer',
  },
  refusal: {
    nameTaken: 'Méthode porte un nom fixe.',
    nameTakenBy: () => 'Méthode porte un nom fixe.',
    retired: 'Méthode est retirée.',
  },
};
